import React, { useState, useEffect } from 'react';
import { FiX, FiDollarSign } from 'react-icons/fi';

const CURRENCIES = ['USD', 'EUR', 'TRY', 'AED', 'SAR', 'GBP', 'JPY', 'CNY', 'INR'];

const AddItemModal = ({ isOpen, onClose, onAdd, groupId, token, boardBaseCurrency = 'USD' }) => {
  const [formData, setFormData] = useState({
    name: '',
    status: 'Not Started',
    priority: 'Medium',
    startDate: '',
    dueDate: '',
    progress: 0,
    value: '',
    currency: boardBaseCurrency,
    exchangeRate: 1,
  });
  const [showCost, setShowCost] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens or boardBaseCurrency changes
  useEffect(() => {
    if (isOpen) {
      setFormData((f) => ({ ...f, currency: boardBaseCurrency, exchangeRate: 1 }));
      setShowCost(false);
    }
  }, [isOpen, boardBaseCurrency]);

  if (!isOpen) return null;

  const baseEquivalent = (() => {
    const v = parseFloat(formData.value);
    const r = parseFloat(formData.exchangeRate);
    if (!v || !r) return null;
    if (formData.currency === boardBaseCurrency) return v;
    return v / r;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        groupId,
        assignees: [],
        dependencies: [],
        fileLinks: [],
        value: parseFloat(formData.value) || 0,
        currency: formData.currency || boardBaseCurrency,
        exchangeRate: parseFloat(formData.exchangeRate) || 1,
      };
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        onAdd(data.item);
        setFormData({
          name: '', status: 'Not Started', priority: 'Medium',
          startDate: '', dueDate: '', progress: 0,
          value: '', currency: boardBaseCurrency, exchangeRate: 1,
        });
        setShowCost(false);
        onClose();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create item');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (n, c) => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: c, maximumFractionDigits: 2 }).format(n);
    } catch (_e) { return `${n.toFixed(2)} ${c}`; }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">Add New Task</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Task Name *</label>
              <input
                type="text"
                className="input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="item-name-input"
              />
            </div>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="input"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="Not Started">Not Started</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Done">Done</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  className="input"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="input"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="input"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                />
              </div>
            </div>

            {/* Cost / Finance toggle */}
            {!showCost ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowCost(true)}
                data-testid="add-cost-toggle"
                style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
              >
                <FiDollarSign size={14} /> Add Client Price & Currency (optional)
              </button>
            ) : (
              <div className="cost-block" data-testid="cost-block">
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span><FiDollarSign size={14} style={{ verticalAlign: 'middle' }} /> Client Price</span>
                    <button
                      type="button"
                      onClick={() => { setShowCost(false); setFormData({ ...formData, value: '', currency: boardBaseCurrency, exchangeRate: 1 }); }}
                      style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}
                    >Remove</button>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder="0.00"
                    data-testid="task-cost-input"
                  />
                </div>
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Currency</label>
                    <select
                      className="input"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value, exchangeRate: e.target.value === boardBaseCurrency ? 1 : formData.exchangeRate })}
                      data-testid="task-cost-currency"
                    >
                      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      {!CURRENCIES.includes(formData.currency) && <option value={formData.currency}>{formData.currency}</option>}
                    </select>
                  </div>
                  {formData.currency !== boardBaseCurrency ? (
                    <div className="form-group">
                      <label className="form-label">1 {boardBaseCurrency} = ? {formData.currency}</label>
                      <input
                        type="number"
                        step="0.0001"
                        className="input"
                        value={formData.exchangeRate}
                        onChange={(e) => setFormData({ ...formData, exchangeRate: e.target.value })}
                        placeholder="e.g. 46.2"
                        data-testid="task-cost-rate"
                      />
                    </div>
                  ) : (
                    <div className="form-group">
                      <label className="form-label">Rate</label>
                      <input type="text" className="input" value="1.0 (same currency)" disabled />
                    </div>
                  )}
                </div>
                {baseEquivalent !== null && formData.currency !== boardBaseCurrency && (
                  <p className="form-helper" style={{ marginTop: -4 }}>
                    ≈ <strong>{formatMoney(baseEquivalent, boardBaseCurrency)}</strong> in project base
                  </p>
                )}
              </div>
            )}

            {error && <div className="form-error">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading} data-testid="submit-item">
              {loading ? 'Adding...' : 'Add Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddItemModal;
